import { forwardRef, memo, type ButtonHTMLAttributes } from 'react';
import { cva, type CVAVariantProps } from '../../utils/variants';
import { cn } from '../../utils/cn';
import { getFocusClasses, getDisabledProps, getAriaProps } from '../../utils/accessibility';
import type { BaseComponentProps, IconProps, LoadingProps } from '../../types';

/**
 * Varianti del Button usando class-variance-authority
 * AGGIORNATO: Usa variabili tema per supportare temi dinamici
 */
const buttonVariants = cva(
  // Base styles
  'inline-flex items-center justify-center font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed border',
  {
    variants: {
      variant: {
        primary: 'bg-primary hover:bg-primary-focus active:opacity-90 text-primary-content border-primary',
        secondary: 'bg-secondary hover:bg-secondary-focus active:opacity-90 text-secondary-content border-secondary',
        success: 'bg-success hover:opacity-90 active:opacity-80 text-success-content border-success',
        error: 'bg-error hover:opacity-90 active:opacity-80 text-error-content border-error',
        warning: 'bg-warning hover:opacity-90 active:opacity-80 text-warning-content border-warning',
        info: 'bg-info hover:opacity-90 active:opacity-80 text-info-content border-info',
        ghost: 'bg-transparent hover:bg-base-200 active:bg-base-300 text-base-content border-transparent',
        link: 'bg-transparent hover:underline text-primary border-transparent p-0',
      },
      size: {
        xs: 'text-xs px-2 py-1 rounded',
        sm: 'text-sm px-3 py-1.5 rounded-md',
        md: 'text-base px-4 py-2 rounded-md',
        lg: 'text-lg px-6 py-3 rounded-lg',
        xl: 'text-xl px-8 py-4 rounded-lg',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  }
);

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'>,
    BaseComponentProps,
    IconProps,
    LoadingProps,
    CVAVariantProps<typeof buttonVariants> {
  /**
   * Se true, il button occupa tutta la larghezza disponibile
   */
  fullWidth?: boolean;
  /**
   * Se true, il button è disabilitato
   */
  disabled?: boolean;
  /**
   * Label ARIA per accessibilità
   */
  'aria-label'?: string;
}

/**
 * Componente Button
 * 
 * Button versatile con supporto per varianti, dimensioni, icone e stati di caricamento.
 * Completamente accessibile con supporto ARIA.
 * Ottimizzato con React.memo per prevenire re-render non necessari.
 * 
 * @example
 * ```tsx
 * <Button variant="primary" size="md">Click me</Button>
 * <Button variant="success" loading>Loading...</Button>
 * <Button icon={<Icon />} iconPosition="left">With Icon</Button>
 * ```
 */
const ButtonComponent = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      disabled,
      loading,
      loadingText,
      icon,
      iconPosition = 'left',
      children,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const disabledProps = getDisabledProps(isDisabled);
    const ariaProps = getAriaProps(
      {
        'aria-label': props['aria-label'],
      },
      typeof children === 'string' ? children : undefined
    );

    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          buttonVariants({ variant, size, fullWidth }),
          getFocusClasses(),
          className
        )}
        aria-busy={loading}
        {...disabledProps}
        {...ariaProps}
        {...props}
      >
        {loading && (
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        
        {!loading && icon && iconPosition === 'left' && (
          <span className="mr-2" aria-hidden="true">{icon}</span>
        )}
        
        {loading ? loadingText || children : children}
        
        {!loading && icon && iconPosition === 'right' && (
          <span className="ml-2" aria-hidden="true">{icon}</span>
        )}
      </button>
    );
  }
);

ButtonComponent.displayName = 'Button';

export const Button = memo(ButtonComponent);

